import {
  Box,
  Button as ChakraButton,
  ButtonProps,
  Flex,
  IconButton,
  useDisclosure,
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { Link } from 'react-router';
import { MouseEventHandler, ReactNode, useState } from 'react';
import fonts from 'src/theme/fonts';
import colors from 'src/theme/colors';
import hexToRgb from 'src/utils/hexToRgb';
import { MyShowsContextProvider } from 'src/components/my-shows/MyShowsContext';
import { useCreateEpisode } from 'src/pages/episodes/top-section/TopSection';
import useCanCreateEpisode from 'src/pages/episodes/useCanCreateEpisode';
import UpgradeSubscriptionDialog from 'src/pages/episodes/UpgradeSubscriptionDialog';
import usePageSelected from '../../hooks/usePageSelected';
import Icons from '../../Icons';
import { useMenuContext } from '../../MenuContext';
import MyShowsMenuButton from './MyShowsMenuButton';
import CelloLaunchButton from './CelloLaunchButton';

interface NavButtonProps extends ButtonProps {
  page:
    | 'episodes'
    | 'call-recorder'
    | 'clip-genie'
    | 'library'
    | 'settings'
    | 'help-center'
    | 'account'
    | 'statistics'
    | 'my-shows'
    | 'invite-friends';
}

export default function NavButton({ page, ...props }: NavButtonProps) {
  const [menuState] = useMenuContext();
  const selected = usePageSelected();

  switch (page) {
    case 'episodes': {
      return (
        <Box position="relative">
          <Button
            text="my episodes"
            Icon={
              <Icons.Episodes
                color={selected.episodes === true ? 'podcastPurple800' : colors.white}
              />
            }
            isSelected={selected.episodes}
            to="/episodes"
            data-intercom-target="My Episodes"
            {...props}
          />
          {menuState === 'open' && <CreateEpisodeButton isSelected={selected.episodes} />}
        </Box>
      );
    }
    case 'call-recorder': {
      return (
        <Box position="relative">
          <Button
            text="record"
            Icon={
              <Icons.Recorder color={selected.callRecorder ? 'podcastPurple800' : colors.white} />
            }
            isSelected={selected.callRecorder}
            to="/record-call"
            data-intercom-target="Record"
            {...props}
          />
        </Box>
      );
    }
    case 'clip-genie': {
      return (
        <Button
          text="Clip Genie"
          Icon={
            <Icons.ClipGenie
              color={selected.clipGenie === true ? 'podcastPurple800' : colors.white}
            />
          }
          isSelected={selected.clipGenie}
          to="/clip-genie"
          data-intercom-target="Clip Genie"
          {...props}
        />
      );
    }
    case 'library': {
      return (
        <Button
          text="my library"
          Icon={
            <Icons.Library color={selected.library === true ? 'podcastPurple800' : colors.white} />
          }
          isSelected={selected.library}
          to="/library"
          data-intercom-target="My Library"
          {...props}
        />
      );
    }
    case 'statistics': {
      return (
        <Button
          text="Statistics"
          Icon={
            <Icons.Statistics
              color={selected.statistics === true ? 'podcastPurple800' : colors.white}
            />
          }
          isSelected={selected.statistics}
          to="/statistics"
          {...props}
        />
      );
    }
    case 'settings': {
      return (
        <Button
          text="show settings"
          Icon={
            <Icons.Settings
              color={selected.settings === true ? 'podcastPurple800' : colors.white}
            />
          }
          isSelected={selected.settings}
          to="/settings"
          data-intercom-target="Show Settings"
          {...props}
        />
      );
    }
    case 'help-center': {
      return (
        <Button
          text="help center"
          Icon={<Icons.Question />}
          isSelected={false}
          as="a"
          // @ts-ignore
          target="_blank"
          to="https://help.alitu.com/en"
          {...props}
          data-intercom-target="Help Center"
        />
      );
    }
    case 'my-shows': {
      return (
        <MyShowsContextProvider>
          <MyShowsMenuButton menuState={menuState} />
        </MyShowsContextProvider>
      );
    }
    case 'invite-friends': {
      return <CelloLaunchButton />;
    }
    default: {
      throw new Error(`Unhandled nav button ${page}`);
    }
  }
}

function Button({
  text,
  Icon,
  isSelected,
  ...props
}: ButtonProps & {
  to: string;
  text: string;
  Icon: ReactNode;
  isSelected: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}) {
  const [menuState] = useMenuContext();
  const [isHovered, setIsHovered] = useState(false);
  const sharedProps = isSelected
    ? {
        bg: colors.white,
        color: colors.podcastPurple800,
        borderRadius: '12px',
        _active: { bg: colors.white, color: hexToRgb(colors.podcastPurple800, 0.8) },
      }
    : {
        bg: 'transparent',
        color: colors.podcastShade100,
        _active: { color: hexToRgb(colors.podcastShade200, 0.8) },
      };

  const hoverProps = isSelected
    ? {
        boxShadow: '0px 2px 6px rgb(101 85 205 / 30%)',
      }
    : {
        bg: menuState === 'closed' ? colors.podcastPurple800 : hexToRgb(colors.white, 0.1),
      };

  if (menuState === 'closed') {
    return (
      <OpenMenuButton
        as={Link}
        visual="flat"
        w={isHovered ? '100%' : '48px'}
        height="48px"
        aria-label={text}
        Icon={Icon}
        {...sharedProps}
        {...props}
        {...(isHovered ? hoverProps : {})}
        padding={isHovered ? props._hover?.padding || '0 9px' : undefined}
        justifyContent="center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isHovered ? text : null}
      </OpenMenuButton>
    );
  }

  return (
    <OpenMenuButton
      aria-label={text}
      Icon={Icon}
      {...sharedProps}
      {...props}
      _hover={hoverProps}
      justifyContent="flex-start"
      fontSize="16px"
    >
      {text}
    </OpenMenuButton>
  );
}

function OpenMenuButton({
  Icon,
  children,
  to,
  onClick,
  ...props
}: ButtonProps & { Icon: ReactNode; to: string }) {
  return (
    <ChakraButton
      asChild
      onClick={onClick}
      visual="flat"
      fontFamily={fonts.bold}
      justifyContent="flex-start"
      textTransform="capitalize"
      w="100%"
      height="48px"
      px="15px"
      transitionDuration="150ms"
      zIndex={20}
      justifySelf="flex-start"
      alignItems="center"
      // gridGap="12px"
      {...props}
    >
      <Link to={to}>
        {/* Icon wrapper with fixed width prevents 'jumping' icons on hover */}
        <Flex w="28px" justifyContent="center">
          {Icon}
        </Flex>
        {children}
      </Link>
    </ChakraButton>
  );
}

function CreateEpisodeButton({ isSelected }: { isSelected: boolean }) {
  const canCreateEpisode = useCanCreateEpisode();
  const createEpisode = useCreateEpisode();
  const { open, onOpen, onClose } = useDisclosure();
  return (
    <>
      <UpgradeSubscriptionDialog open={open} onClose={onClose} />
      <IconButton
        onClick={() => {
          if (!canCreateEpisode) {
            onOpen();
            return;
          }
          createEpisode();
        }}
        aria-label="create episode"
        borderRadius="8px"
        minWidth="30px"
        height="30px"
        bg="rgba(0, 0, 0, 0.2)"
        bgGradient="none"
        boxShadow="none"
        bgColor={isSelected === true ? colors.podcastPurple : hexToRgb(colors.black, 0.2)}
        position="absolute"
        right="9px"
        top="9px"
        zIndex={21}
        alignContent="center"
      >
        <ChakraIcon width="12px" height="12px">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12">
            <path
              d="M1 6h10M6 1v10"
              stroke={colors.white}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ChakraIcon>
      </IconButton>
    </>
  );
}
